import { api } from '@/api/client';

export interface NoteSummary {
  id: string;
  title: string;
  slug: string;
  parent_id: string | null;
  owner_id: string;
  created_at: string;
  updated_at: string;
  current_version: number;
  char_count: number;
  pinned: boolean;
  is_template: boolean;
  tag_ids: string[];
}

export interface NotePath {
  tenant_slug: string;
  segments: string[];
  url: string;
}

export interface NoteDetail extends NoteSummary {
  ciphertext: string;
  nonce: string;
  dek_wrapped: string;
}

export interface NoteCreate {
  title: string;
  slug?: string;
  parent_id?: string | null;
  ciphertext: string;
  nonce: string;
  dek_wrapped: string;
}

export interface NoteUpdate extends NoteCreate {
  /**
   * {user_id -> base64_dek_wrapped} pentru TOȚI userii cu share activ.
   * Obligatoriu la multi-share (altfel ceilalți rămân cu DEK stale).
   */
  dek_wraps?: Record<string, string>;
  change_reason?: string;
}

export interface NoteVersionSummary {
  id: string;
  version: number;
  title: string;
  parent_id: string | null;
  changed_by: string;
  changed_at: string;
  change_reason: string | null;
  change_source: string | null;
}

export interface NoteVersionDetail extends NoteVersionSummary {
  ciphertext: string;
  nonce: string;
  dek_wrapped: string;
}

export interface NoteShareListItem {
  user_id: string;
  email: string;
  permission: string;
  created_at: string;
}

export interface NoteShareCreate {
  user_id: string;
  dek_wrapped: string;
  permission: 'read' | 'write';
}

export const notesApi = {
  list: (tenantId: string) => api.get<NoteSummary[]>(`/tenants/${tenantId}/notes`),
  get: (id: string) => api.get<NoteDetail>(`/notes/${id}`),
  create: (tenantId: string, req: NoteCreate) =>
    api.post<NoteDetail>(`/tenants/${tenantId}/notes`, req),
  update: (id: string, req: NoteUpdate) => api.patch<NoteDetail>(`/notes/${id}`, req),
  move: (id: string, parentId: string | null) =>
    api.patch<NoteSummary>(`/notes/${id}/move`, { parent_id: parentId }),
  rename: (id: string, title: string) =>
    api.patch<NoteSummary>(`/notes/${id}/rename`, { title }),
  updateSlug: (id: string, slug: string) =>
    api.patch<NoteSummary>(`/notes/${id}/slug`, { slug }),
  getPath: (id: string) => api.get<NotePath>(`/notes/${id}/path`),
  delete: (id: string) => api.delete<null>(`/notes/${id}`),

  listTemplates: (tenantId: string) =>
    api.get<NoteSummary[]>(`/tenants/${tenantId}/note-templates`),
  setTemplate: (id: string, isTemplate: boolean) =>
    api.patch<NoteSummary>(`/notes/${id}/template`, { is_template: isTemplate }),

  pin: (id: string) => api.post<null>(`/notes/${id}/pin`),
  unpin: (id: string) => api.delete<null>(`/notes/${id}/pin`),

  listVersions: (id: string) =>
    api.get<NoteVersionSummary[]>(`/notes/${id}/versions`),

  getVersion: (id: string, version: number) =>
    api.get<NoteVersionDetail>(`/notes/${id}/versions/${version}`),

  listShares: (id: string) =>
    api.get<NoteShareListItem[]>(`/notes/${id}/shares`),

  addShare: (id: string, req: NoteShareCreate) =>
    api.post<NoteShareListItem>(`/notes/${id}/shares`, req),

  /** Rotește DEK + (opțional) revocă. Zero-acces post-revoke. */
  rotate: (
    id: string,
    req: {
      ciphertext: string;
      nonce: string;
      dek_wraps: Record<string, string>;
      revoke?: string[];
      change_reason?: string;
    },
  ) => api.post<NoteDetail>(`/notes/${id}/rotate`, req),
};
